
























































import { Component, Vue, Prop, PropSync } from 'vue-property-decorator';
import { EntityLocation } from '@/api';
import { Icon, LatLng, LatLngBounds, LatLngTuple, Map, Marker } from 'leaflet';
import { EntityLocationService } from '@/services/entity-location-service';
import {
	EntityMapService,
	MarkerData,
	TransceiverWithLocationData,
} from './services/entity-map-service';
import { LMap } from 'vue2-leaflet';
import { Entity } from '@/models';

@Component({
	components: {},
})
export default class EntityMap<T extends Entity> extends Vue {
	private APIKey = 'd01fbtg0ar02se9fxybaqv0jxhw';
	public tileProviders = [
		{
			name: 'ESRI',
			visible: true,

			url:
				'https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token=secret',
			attribution: 'Powered by <a href="http://www.esri.com/">Esri</a>',
		},
		{
			name: 'Open Street Map',
			visible: false,

			url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
			attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
		},
		{
			name: 'LINZ Topo 50',
			visible: false,

			url:
				'https://basemaps.linz.govt.nz/v1/tiles/aerial/EPSG:3857/{z}/{x}/{y}.webp?api=' +
				this.APIKey,
			attribution: '<a href=“http://data.linz.govt.nz”>Sourced from LINZ. CC BY 4.0</a>',
		},
	];
	public zoom = 15;
	public siteMarker: {
		entityId: string;
		entityType: string;
		icon: Icon;
		location: { lat: number; lng: number };
	} | null = null;
	public clusterOptions = {
		spiderfyOnMaxZoom: true,
	};
	public markerEditing = false;

	@Prop({ required: false, type: Array, default: [] })
	public transceivers!: TransceiverWithLocationData[];

	@Prop({ required: false, type: Array })
	private entityLocations:
		| {
				entityType: string;
				location: EntityLocation;
				entity: T;
		  }[]
		| undefined;

	@PropSync('mapcenter')
	public center!: { lat: number; lng: number };

	public get markers(): MarkerData[] {
		return this.entityMapService.markers;
	}

	private entityMapService: EntityMapService<T>;
	private map: Map | null = null;

	constructor() {
		super();
		this.entityMapService = new EntityMapService(this.transceivers, this.entityLocations ?? []);
	}

	public mounted(): void {
		this.entityMapService.makeEntityMarkers();
		this.siteMarker = this.entityMapService.makeSiteMarker(this.center, this.$route.params.siteId);
		setTimeout(function() {
			window.dispatchEvent(new Event('resize'));
		}, 250);
	}

	public zoomUpdated(zoom: number): void {
		this.zoom = zoom;
	}

	public centerUpdated(center: LatLng): void {
		this.center = center;
	}

	public toggleMarkerEditing(): void {
		this.markerEditing = !this.markerEditing;
		if (this.markerEditing === true) {
			this.$bvToast.toast('You can drag markers and their location will update', {
				toaster: 'b-toaster-bottom-center',
				title: 'Editing enabled',
			});
		}
	}

	public onMapReady(): void {
		this.map = (this.$refs.map as LMap).mapObject;
		const bounds = this.getMapBounds();
		this.map.invalidateSize();
		this.map.fitBounds(bounds, { maxZoom: 15 });
	}

	public async onMarkerMoved(
		markerChangeEvent: { target: Marker },
		marker: { extraData: Record<string, any>; marker: Marker },
	): Promise<void> {
		const newCoords = markerChangeEvent.target.getLatLng();
		EntityLocationService.updateEntityLocation(
			marker.extraData.locationId, // need to get this from marker changed event. wrap as above,
			{ latitude: newCoords.lat, longitude: newCoords.lng },
		);
	}

	private getMapBounds(): LatLngBounds {
		const entityLatLngs: LatLngTuple[] = this.markers.map(markerData => {
			const latLng = markerData.marker.getLatLng();
			return [latLng.lat, latLng.lng];
		});
		const siteLatLng: LatLngTuple | null = this.siteMarker
			? [this.siteMarker.location.lat, this.siteMarker.location.lng]
			: null;
		if (siteLatLng) {
			entityLatLngs.push(siteLatLng);
		}
		return new LatLngBounds(entityLatLngs);
	}
}
